import { useId } from 'react';
import { useAppConfig } from '@/hooks/queries';
import { useLeadAccount } from '@/hooks/leverageQueries';
import { Field } from '@/components/ui';
import { PasswordInput } from '@/components/PasswordInput';
import '@/components/leverage/leverage.css';

export interface AccountDraft {
  accountLogin: string;
  accountPassword: string;
}

interface ClientAccountFieldsProps {
  /** O cliente escolhido; vazio, nada aparece. */
  leadId: string;
  value: AccountDraft;
  onChange: (patch: Partial<AccountDraft>) => void;
}

/**
 * A conta do cliente na corretora, quando ainda falta login ou senha na ficha
 * dele: quem opera a alavancagem entra com eles. O servidor só completa — nunca
 * troca o que já existe (trocar é na ficha do cliente). A senha vai cifrada.
 */
export function ClientAccountFields({ leadId, value, onChange }: ClientAccountFieldsProps) {
  const id = useId();
  const account = useLeadAccount(leadId);
  const { data: config } = useAppConfig();
  const needsLogin = account.data ? !account.data.accountLogin : false;
  const needsPassword = account.data ? !account.data.hasAccountPassword : false;
  // Sem a chave de criptografia no servidor, a senha não pode ser guardada.
  const canStorePassword = config?.credentialsEnabled !== false;

  if (!needsLogin && !needsPassword) return null;

  return (
    <section className="lev-account" aria-labelledby={`${id}-conta`}>
      <div id={`${id}-conta`} className="lev-account-title">Conta do cliente na corretora</div>
      <p className="lev-account-lede muted">
        {needsLogin && needsPassword
          ? 'Este cliente ainda não tem login nem senha guardados.'
          : needsLogin
            ? 'Este cliente ainda não tem o login guardado.'
            : 'Este cliente ainda não tem a senha guardada.'}{' '}
        Quem executa as operações entra na conta com eles.
      </p>
      <div className={needsLogin && needsPassword ? 'grid grid2' : undefined}>
        {needsLogin ? (
          <Field label="Login da conta">
            <input
              className="inp"
              value={value.accountLogin}
              onChange={(event) => onChange({ accountLogin: event.target.value })}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Usuário ou e-mail na corretora"
              maxLength={200}
            />
          </Field>
        ) : null}
        {needsPassword ? (
          <div className="field">
            <label className="field-label" htmlFor={`${id}-senha`}>Senha da conta</label>
            {canStorePassword ? (
              <>
                <PasswordInput
                  id={`${id}-senha`}
                  value={value.accountPassword}
                  onChange={(accountPassword) => onChange({ accountPassword })}
                  autoComplete="new-password"
                  placeholder="A senha do cliente na corretora"
                  maxLength={200}
                />
                <span className="field-hint">Guardada criptografada. Só o responsável e os administradores veem.</span>
              </>
            ) : (
              <span className="field-hint">Guardar senha exige a chave de criptografia configurada no servidor.</span>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
